import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Input,
  Message,
  Segment,
  Grid,
  Icon,
  Tab,
  Image,
} from "semantic-ui-react";
import PropTypes from "prop-types";
import { fileApi } from "@api/files";
import {
  validateISBN,
  fetchBookDataFromAPI,
  validateAPIConfig,
  mapCommonBookData,
  generateCallNumber,
  ISBNDB_API_KEY,
  ISBNDB_API_URL,
} from "@utils/isbnUtils";
import {
  validateCoverImage,
  createFilePreview,
  revokeFilePreview,
  FILE_SIZE_LIMITS,
  COMMON_IMAGE_TYPES,
  formatFileSize,
} from "@utils/fileUtils";

const STYLES = {
  segment: { margin: "17px 69px", backgroundColor: "#f8f9fa" },
  header: { margin: "0 0 10px 0", color: "#225745" },
  subtitle: { color: "#666", fontSize: "14px", margin: "0 0 15px 0" },
  warning: { color: "red", fontSize: "13px", margin: "10px 0 15px 5px" },
  note: { fontSize: "13px", marginTop: "10px", color: "#666" },
};

export const ISBNLookup = ({
  onDataFetched,
  onFileSelected,
  isVisible = true,
  documentPid = null,
  currentCover = null,
  showTabs = true,
}) => {
  const [isbn, setIsbn] = useState("");
  const [isbnLoading, setIsbnLoading] = useState(false);
  const [isbnError, setIsbnError] = useState(null);
  const [isbnSuccess, setIsbnSuccess] = useState(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(currentCover);
  const [coverLoading, setCoverLoading] = useState(false);
  const [coverError, setCoverError] = useState(null);
  const [coverSuccess, setCoverSuccess] = useState(null);
  const fileInputRef = useRef(null);

  const isCreating = !documentPid || documentPid === "create";

  useEffect(() => {
    if (currentCover !== previewUrl && !selectedFile) {
      setPreviewUrl(currentCover);
    }
  }, [currentCover, previewUrl, selectedFile]);

  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith("blob:")) {
        revokeFilePreview(previewUrl);
      }
    };
  }, [previewUrl]);

  const mapISBNDataToForm = (bookData) => {
    const mappedData = {
      ...mapCommonBookData(bookData),
      document_type: "BOOK",
      call_number: generateCallNumber(bookData),
    };

    const isbn = bookData.isbn13 || bookData.isbn;
    if (isbn) {
      mappedData.cover_metadata = { ISBN: isbn };
    }

    if (bookData.subjects?.length > 0) {
      mappedData.keywords = bookData.subjects.slice(0, 5).map((subject) => ({
        value: subject,
        source: "ISBN_API",
      }));
    }

    if (mappedData.number_of_pages) {
      mappedData.number_of_pages = mappedData.number_of_pages.toString();
    }

    return mappedData;
  };

  const clearMessages = (type) => {
    if (type === "isbn") {
      setIsbnError(null);
      setIsbnSuccess(null);
    } else {
      setCoverError(null);
      setCoverSuccess(null);
    }
  };

  const fetchISBNData = async () => {
    if (!isbn.trim()) {
      setIsbnError("Please enter an ISBN");
      return;
    }

    if (!validateISBN(isbn)) {
      setIsbnError("Please enter a valid ISBN (10 or 13 digits)");
      return;
    }

    if (!validateAPIConfig(ISBNDB_API_KEY)) {
      setIsbnError(
        "Please configure your ISBN API key in environment variables"
      );
      return;
    }

    setIsbnLoading(true);
    clearMessages("isbn");

    try {
      const bookData = await fetchBookDataFromAPI(
        isbn,
        ISBNDB_API_KEY,
        ISBNDB_API_URL
      );

      // Check if bookData is null/undefined (ISBN not found in API)
      if (!bookData) {
        setIsbnError(
          `ISBN "${isbn}" not found. Please verify the ISBN or try manual entry.`
        );
        setIsbnLoading(false);
        return;
      }

      const mappedData = mapISBNDataToForm(bookData);
      onDataFetched(mappedData);
      setIsbnSuccess(
        `Book data successfully imported with auto-generated call number: ${
          mappedData.call_number || "N/A"
        }. Please review and adjust Library Sections if needed.`
      );
      setIsbn("");
    } catch (error) {
      // Handle API errors or network issues
      if (error.message && error.message.includes("not found")) {
        setIsbnError(
          `ISBN "${isbn}" not found. Please verify the ISBN or try manual entry.`
        );
      } else if (error.message && error.message.includes("rate limit")) {
        setIsbnError(
          "API rate limit exceeded. Please wait a moment and try again."
        );
      } else {
        setIsbnError(
          "Failed to fetch book data. Please check your internet connection and try again."
        );
      }
    } finally {
      setIsbnLoading(false);
    }
  };

  const handleISBNInputChange = (e) => {
    setIsbn(e.target.value);
    clearMessages("isbn");
  };

  const handleISBNClearInput = () => {
    setIsbn("");
    clearMessages("isbn");
  };

  const handleISBNKeyPress = (event) => {
    if (event.key === "Enter") {
      fetchISBNData();
    }
  };

  const cleanupPreviewUrl = () => {
    if (previewUrl?.startsWith("blob:")) {
      revokeFilePreview(previewUrl);
    }
  };

  const resetFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    clearMessages("cover");

    if (!file) {
      setSelectedFile(null);
      cleanupPreviewUrl();
      setPreviewUrl(currentCover);
      return;
    }

    // Use centralized validation
    const validation = validateCoverImage(file);
    if (!validation.valid) {
      setCoverError(validation.errors.join("; "));
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    cleanupPreviewUrl();

    const objectUrl = createFilePreview(file);
    setPreviewUrl(objectUrl);
  };

  const uploadCoverImage = async () => {
    if (!selectedFile) {
      setCoverError("Please select an image file first");
      return;
    }

    setCoverLoading(true);
    clearMessages("cover");

    if (isCreating) {
      if (onFileSelected) {
        onFileSelected(selectedFile);
      }

      onDataFetched({
        cover_preview_url: previewUrl,
        cover_metadata_preview: {
          type: "manual_upload",
          filename: selectedFile.name,
          file_size: selectedFile.size,
          file_type: selectedFile.type,
        },
      });

      setCoverSuccess(
        "Cover image ready! It will be uploaded when you save the document."
      );
      resetFileInput();
    } else {
      const response = await fileApi.cover.upload(documentPid, selectedFile);

      if (!response.data) {
        throw new Error("Invalid response: missing data");
      }

      const coverData = {
        cover_metadata: response.data.cover_metadata,
        cover_urls: response.data.cover_urls,
      };

      onDataFetched(coverData);
      setCoverSuccess("Cover image uploaded successfully!");
      setSelectedFile(null);
      resetFileInput();

      if (response.data.cover_urls) {
        const newCoverUrl =
          response.data.cover_urls.large ||
          response.data.cover_urls.medium ||
          response.data.cover_urls.small;
        if (newCoverUrl) {
          cleanupPreviewUrl();
          setPreviewUrl(newCoverUrl);
        }
      }
    }

    setCoverLoading(false);
  };

  const removeCover = async () => {
    if (isCreating) {
      cleanupPreviewUrl();
      setPreviewUrl(null);
      setSelectedFile(null);
      resetFileInput();
      onDataFetched({ pending_cover: null });
      setCoverSuccess("Cover selection cleared");
      return;
    }

    if (!documentPid) {
      setCoverError("Cannot remove cover: Document PID not found");
      return;
    }

    setCoverLoading(true);
    clearMessages("cover");

    await fileApi.cover.delete(documentPid);

    cleanupPreviewUrl();
    setPreviewUrl(null);
    setSelectedFile(null);
    resetFileInput();
    onDataFetched({ cover_metadata: null, cover_urls: null });
    setCoverSuccess("Cover image removed successfully");
    setCoverLoading(false);
  };

  const clearCoverSelection = () => {
    setSelectedFile(null);
    cleanupPreviewUrl();
    setPreviewUrl(currentCover);
    clearMessages("cover");
    resetFileInput();

    if (isCreating) {
      onDataFetched({ pending_cover: null });
    }
  };

  const renderISBNTab = () => (
    <div>
      <h3 style={STYLES.header}>
        <Icon name="book" />
        ISBN Lookup
      </h3>
      <strong style={STYLES.subtitle}>
        Enter an ISBN to automatically fill form fields with book information
        and generate a Dewey Decimal call number.
      </strong>

      <Grid>
        <Grid.Row>
          <Grid.Column width={12}>
            <Input
              fluid
              placeholder="Enter ISBN (10 or 13 digits, e.g., 9780123456789)"
              value={isbn}
              onChange={handleISBNInputChange}
              onKeyPress={handleISBNKeyPress}
              disabled={isbnLoading}
              icon={
                isbn
                  ? {
                      name: "remove",
                      circular: true,
                      link: true,
                      onClick: handleISBNClearInput,
                    }
                  : null
              }
            />
            <p style={STYLES.warning}>
              Skip this if you're going to manually catalog a document.
            </p>
          </Grid.Column>
          <Grid.Column width={4}>
            <Button
              primary
              fluid
              loading={isbnLoading}
              onClick={fetchISBNData}
              disabled={!isbn.trim() || isbnLoading}
            >
              <Icon name="search" />
              {isbnLoading ? "Loading.." : "Lookup"}
            </Button>
          </Grid.Column>
        </Grid.Row>

        {isbnError && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Message negative onDismiss={() => clearMessages("isbn")}>
                <Message.Header>Error</Message.Header>
                <p>{isbnError}</p>
              </Message>
            </Grid.Column>
          </Grid.Row>
        )}

        {isbnSuccess && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Message positive onDismiss={() => clearMessages("isbn")}>
                <Message.Header>Success</Message.Header>
                <p>{isbnSuccess}</p>
                <p style={STYLES.note}>
                  <strong>Note:</strong> Please manually select the language
                  from the dropdown and add additional subjects if needed using
                  the vocabulary widgets.
                </p>
              </Message>
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>
    </div>
  );

  const renderManualCoverTab = () => (
    <div>
      <h3 style={STYLES.header}>
        <Icon name="camera" />
        Manual Cover Upload
      </h3>
      <strong style={STYLES.subtitle}>
        Upload a cover image for books without ISBN or when automatic lookup
        fails.
      </strong>

      {isCreating && (
        <Message info>
          <Message.Header>Document Creation Mode</Message.Header>
          <p>
            Cover will be uploaded automatically when you save the document.
          </p>
        </Message>
      )}

      {previewUrl && (
        <Grid.Row style={{ marginBottom: "15px", textAlign: "center" }}>
          <Grid.Column width={16}>
            <Image
              src={previewUrl}
              size="small"
              bordered
              centered
              style={{ maxHeight: "200px", objectFit: "contain" }}
              onError={() => setCoverError("Failed to load image preview")}
            />
            {(currentCover || (isCreating && previewUrl)) && (
              <div style={{ marginTop: "10px" }}>
                <Button
                  color="red"
                  size="small"
                  onClick={removeCover}
                  disabled={coverLoading}
                  loading={coverLoading}
                >
                  <Icon name="trash" />
                  {isCreating ? "Clear Cover" : "Remove Cover"}
                </Button>
              </div>
            )}
          </Grid.Column>
        </Grid.Row>
      )}

      <Grid>
        <Grid.Row>
          <Grid.Column width={12}>
            <Input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              disabled={coverLoading}
              style={{ width: "100%" }}
            />
            <div style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>
              Supported formats:{" "}
              {COMMON_IMAGE_TYPES.map((type) =>
                type.split("/")[1].toUpperCase()
              ).join(", ")}{" "}
              (max {formatFileSize(FILE_SIZE_LIMITS.MAX_5MB)})
            </div>
          </Grid.Column>

          <Grid.Column width={4}>
            <Button
              primary
              fluid
              loading={coverLoading}
              onClick={uploadCoverImage}
              disabled={!selectedFile || coverLoading}
            >
              <Icon name={isCreating ? "save" : "upload"} />
              {coverLoading
                ? "Processing..."
                : isCreating
                ? "Prepare Cover"
                : "Upload Cover"}
            </Button>
          </Grid.Column>
        </Grid.Row>

        {selectedFile && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Button
                size="small"
                onClick={clearCoverSelection}
                disabled={coverLoading}
              >
                <Icon name="remove" />
                Clear Selection
              </Button>
            </Grid.Column>
          </Grid.Row>
        )}

        {coverError && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Message negative onDismiss={() => clearMessages("cover")}>
                <Message.Header>Error</Message.Header>
                <p>{coverError}</p>
              </Message>
            </Grid.Column>
          </Grid.Row>
        )}

        {coverSuccess && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Message positive onDismiss={() => clearMessages("cover")}>
                <Message.Header>Success</Message.Header>
                <p>{coverSuccess}</p>
              </Message>
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>
    </div>
  );

  if (!isVisible) return null;

  if (!showTabs) {
    return (
      <div>
        <div style={{ marginBottom: "30px" }}>{renderISBNTab()}</div>
        <div>{renderManualCoverTab()}</div>
      </div>
    );
  }

  const ISBNTabPane = () => <Tab.Pane>{renderISBNTab()}</Tab.Pane>;
  ISBNTabPane.displayName = "ISBNTabPane";

  const ManualCoverTabPane = () => (
    <Tab.Pane>{renderManualCoverTab()}</Tab.Pane>
  );
  ManualCoverTabPane.displayName = "ManualCoverTabPane";

  const panes = [
    {
      menuItem: { key: "isbn", icon: "book", content: "ISBN Lookup" },
      render: ISBNTabPane,
    },
    {
      menuItem: { key: "manual", icon: "camera", content: "Manual Cover" },
      render: ManualCoverTabPane,
    },
  ];

  return (
    <Segment style={STYLES.segment}>
      <Tab panes={panes} defaultActiveIndex={0} style={{ marginTop: "0" }} />
    </Segment>
  );
};

ISBNLookup.propTypes = {
  onDataFetched: PropTypes.func.isRequired,
  onFileSelected: PropTypes.func,
  isVisible: PropTypes.bool,
  documentPid: PropTypes.string,
  currentCover: PropTypes.string,
  showTabs: PropTypes.bool,
};

ISBNLookup.defaultProps = {
  onFileSelected: null,
  isVisible: true,
  documentPid: null,
  currentCover: null,
  showTabs: true,
};
